import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { TaskService } from '../services/task.service';
import { CreateTaskData, GroupedTasks, TaskStatus, UpdateTaskData } from '../types/task.types';

export const useTasks = () => {
  const [tasks, setTasks] = useState<GroupedTasks>({ pending: [], completed: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const groupedTasks = await TaskService.getTasks();
      setTasks(groupedTasks);
      setError(null);
    } catch (err) {
      setError('Error fetching tasks');
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create task
  const createTask = useCallback(async (data: CreateTaskData) => {
    try {
      const taskData = {
        ...data,
        dueDate: data.dueDate ? new Date(data.dueDate) : undefined
      };

      const newTask = await TaskService.createTask(taskData);
      await fetchTasks(); // Refresh to get updated positions
      return newTask;
    } catch (err) {
      console.error('Error creating task:', err);
      throw err;
    }
  }, [fetchTasks]);

  // Update task
  const updateTask = useCallback(async (taskId: string, data: UpdateTaskData) => {
    try {
      const taskData = {
        ...data,
        dueDate: data.dueDate ? new Date(data.dueDate) : undefined
      };

      const updatedTask = await TaskService.updateTask(taskId, taskData);
      await fetchTasks(); // Refresh to get updated positions
      return updatedTask;
    } catch (err) {
      console.error('Error updating task:', err);
      throw err;
    }
  }, [fetchTasks]);

  // Toggle task status
  const toggleTaskStatus = useCallback(async (taskId: string, currentStatus: TaskStatus) => {
    try {
      const newStatus = currentStatus === TaskStatus.COMPLETED ? TaskStatus.PENDING : TaskStatus.COMPLETED;

      // Store original state for rollback
      const originalTasks = { ...tasks };


      // Find and move the task
      const sourceArray = currentStatus === TaskStatus.COMPLETED ? tasks.completed : tasks.pending;
      const destinationArray = currentStatus === TaskStatus.COMPLETED ? tasks.pending : tasks.completed;

      const taskIndex = sourceArray.findIndex(task => task.id === taskId);
      if (taskIndex !== -1) {
        const [task] = sourceArray.splice(taskIndex, 1);
        task.status = newStatus;
        destinationArray.push(task);

        // Update the state immediately
        setTasks({
          pending: currentStatus === TaskStatus.COMPLETED ? destinationArray : sourceArray,
          completed: currentStatus === TaskStatus.COMPLETED ? sourceArray : destinationArray
        });

        // Make the API call
        try {
          await TaskService.updateTask(taskId, { status: newStatus });
        } catch (error) {
          // Revert changes if the API call fails
          setTasks(originalTasks);
          console.error('Error toggling task status:', error);
          toast.error('Failed to update task status. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error toggling task status:', error);
      toast.error('Failed to update task status. Please try again.');
    }
  }, [tasks]);

  // Reorder task
  const reorderTask = useCallback(async (taskId: string, newPosition: number) => {
    try {
      await TaskService.reorderTask(taskId, newPosition);
      await fetchTasks(); // Refresh to get updated positions
    } catch (error) {
      console.error('Error reordering task:', error);
    }
  }, [fetchTasks]);

  // Update positions
  const updatePositions = useCallback(async (status: TaskStatus, taskIds: string[]) => {
    try {
      await TaskService.updatePositions(status, taskIds);
    } catch (error) {
      console.error("Error updating task positions:", error);
    }
  }, []);

  // Update subtask
  const updateSubtask = useCallback(async (taskId: string, subtaskId: string, title: string) => {
    try {
      // Find the task and update the subtask locally first
      setTasks(prevTasks => {
        const newTasks = { ...prevTasks };
        for (const status of [TaskStatus.PENDING, TaskStatus.COMPLETED]) {
          const taskIndex = newTasks[status].findIndex(t => t.id === taskId);
          if (taskIndex !== -1) {
            const task = { ...newTasks[status][taskIndex] };
            const subtaskIndex = task.subtasks?.findIndex(st => st.id === subtaskId) ?? -1;
            if (subtaskIndex !== -1 && task.subtasks) {
              task.subtasks = [...task.subtasks];
              task.subtasks[subtaskIndex] = { ...task.subtasks[subtaskIndex], title };
              newTasks[status][taskIndex] = task;
            }
            break;
          }
        }
        return newTasks;
      });

      // Update in backend
      await TaskService.updateTask(taskId, {
        subtasks: tasks[TaskStatus.PENDING].find(t => t.id === taskId)?.subtasks ||
          tasks[TaskStatus.COMPLETED].find(t => t.id === taskId)?.subtasks || []
      });
    } catch (error) {
      console.error('Error updating subtask:', error);
      toast.error('Failed to update subtask');
      // Refresh tasks to ensure consistency
      await fetchTasks();
    }
  }, [tasks, fetchTasks]);

  // Delete subtask
  const deleteSubtask = useCallback(async (taskId: string, subtaskId: string) => {
    try {
      // Find the task and remove the subtask locally first
      setTasks(prevTasks => {
        const newTasks = { ...prevTasks };
        for (const status of [TaskStatus.PENDING, TaskStatus.COMPLETED]) {
          const taskIndex = newTasks[status].findIndex(t => t.id === taskId);
          if (taskIndex !== -1) {
            const task = { ...newTasks[status][taskIndex] };
            task.subtasks = task.subtasks?.filter(st => st.id !== subtaskId) || [];
            newTasks[status][taskIndex] = task;
            break;
          }
        }
        return newTasks;
      });

      // Update in backend
      await TaskService.updateTask(taskId, {
        subtasks: tasks[TaskStatus.PENDING].find(t => t.id === taskId)?.subtasks ||
          tasks[TaskStatus.COMPLETED].find(t => t.id === taskId)?.subtasks || []
      });
    } catch (error) {
      console.error('Error deleting subtask:', error);
      toast.error('Failed to delete subtask');
      // Refresh tasks to ensure consistency
      await fetchTasks();
    }
  }, [tasks, fetchTasks]);

  // Reorder subtasks
  const reorderSubtask = async (
    taskId: string,
    action: ['move-up' | 'move-down', string]
  ) => {
    const [direction, subtaskId] = action;
    const task = tasks[TaskStatus.PENDING].find(t => t.id === taskId) ||
      tasks[TaskStatus.COMPLETED].find(t => t.id === taskId);

    if (!task) return;

    const subtaskIndex = task.subtasks.findIndex(s => s.id === subtaskId);
    if (subtaskIndex === -1) return;

    if (direction === 'move-up' && subtaskIndex === 0) return;
    if (direction === 'move-down' && subtaskIndex === task.subtasks.length - 1) return;

    const newSubtasks = [...task.subtasks];
    const targetIndex = direction === 'move-up' ? subtaskIndex - 1 : subtaskIndex + 1;

    [newSubtasks[subtaskIndex], newSubtasks[targetIndex]] =
      [newSubtasks[targetIndex], newSubtasks[subtaskIndex]];

    const newTasks = {
      ...tasks,
      [task.status]: tasks[task.status].map(t =>
        t.id === taskId ? { ...t, subtasks: newSubtasks } : t
      )
    };

    setTasks(newTasks);

    try {
      await TaskService.updateTask(taskId, {
        subtasks: newSubtasks
      });
    } catch (error) {
      console.error('Error reordering subtask:', error);
      toast.error('Failed to reorder subtask');
      setTasks(tasks);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  return {
    tasks,
    loading,
    error,
    createTask,
    updateTask,
    deleteTask: useCallback(async (taskId: string) => {
      try {
        await TaskService.deleteTask(taskId);
        await fetchTasks();
      } catch (err) {
        console.error('Error deleting task:', err);
        throw err;
      }
    }, [fetchTasks]),
    toggleTaskStatus,
    reorderTask,
    addSubtask: useCallback(async (taskId: string, title: string) => {
      try {
        await TaskService.addSubtask(taskId, title);
        await fetchTasks();
      } catch (error) {
        console.error('Error adding subtask:', error);
      }
    }, [fetchTasks]),
    toggleSubtask: useCallback(async (taskId: string, subtaskId: string) => {
      try {
        await TaskService.toggleSubtask(taskId, subtaskId);
        await fetchTasks();
      } catch (error) {
        console.error('Error toggling subtask:', error);
      }
    }, [fetchTasks]),
    updateSubtask,
    deleteSubtask,
    reorderSubtask,
    setTasks,
    updatePositions,
  };
};
